/**
 * Secrets of the native app (VOL-14 lot 5), kept in the iOS Keychain and the
 * Android Keystore by the secure-storage plugin. Never `localStorage`: on the
 * device it is a plain file any backup or rooted phone can read.
 */
export interface SecureStorage {
  get: (key: string) => Promise<string | null>;
  set: (key: string, value: string) => Promise<void>;
  remove: (key: string) => Promise<void>;
}

// Loaded on demand so the website never downloads the plugin. Resolves with the
// module, never the plugin proxy (its `then` would be called as a native method).
let pluginModule: Promise<typeof import('capacitor-secure-storage-plugin')> | null = null;
const loadPluginModule = () =>
  (pluginModule ??= import('capacitor-secure-storage-plugin').catch((error: unknown) => {
    pluginModule = null;
    throw error;
  }));

export const nativeSecureStorage: SecureStorage = {
  get: async (key) => {
    const { SecureStoragePlugin } = await loadPluginModule();
    // The plugin rejects for a missing key instead of returning nothing.
    const { value: keys } = await SecureStoragePlugin.keys();
    if (!keys.includes(key)) return null;
    const { value } = await SecureStoragePlugin.get({ key });
    return value;
  },
  set: async (key, value) => {
    const { SecureStoragePlugin } = await loadPluginModule();
    await SecureStoragePlugin.set({ key, value });
  },
  remove: async (key) => {
    const { SecureStoragePlugin } = await loadPluginModule();
    const { value: keys } = await SecureStoragePlugin.keys();
    if (keys.includes(key)) await SecureStoragePlugin.remove({ key });
  },
};

/** The website never holds a token itself: its session lives in httpOnly cookies. */
export const noSecureStorage: SecureStorage = {
  get: () => Promise.resolve(null),
  set: () => Promise.reject(new Error('No secure storage on the web')),
  remove: () => Promise.resolve(),
};
